<template>
  <div v-if="visible" class="table-slash-picker" @keydown.esc="close">
    <div class="backdrop" @click="close" />
    <div
      ref="panel"
      class="panel"
      role="dialog"
      aria-modal="true"
      tabindex="-1"
      :aria-label="$t(resizing ? 'table.sizePickerResizeTitle' : 'table.sizePickerTitle')"
      @keydown.tab="trapTab"
    >
      <p class="title">{{ $t(resizing ? 'table.sizePickerResizeTitle' : 'table.sizePickerTitle') }}</p>
      <p class="size-label">{{ displayRows }} × {{ displayColumns }}</p>
      <div class="grid" :style="gridStyle" @mouseleave="resetHover" @keydown="onGridKeydown">
        <button
          v-for="cell in gridCells"
          :key="cell.key"
          type="button"
          class="cell"
          :data-cell="cell.key"
          :class="{ active: cell.row <= hoverRows && cell.col <= hoverColumns }"
          :tabindex="cell.row === hoverRows && cell.col === hoverColumns ? 0 : -1"
          :aria-label="cell.label"
          @mouseenter="setHover(cell.row, cell.col)"
          @focus="setHover(cell.row, cell.col)"
          @click="confirmAt(cell.row, cell.col)"
        />
      </div>
      <div class="actions">
        <n-button v-if="!resizing" size="s" :label="$t('table.sizePickerDefault')" @click="confirmDefault" />
        <n-button size="s" :label="$t('general.cancel')" @click="close" />
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { Component, Ref, Vue, Watch } from 'vue-property-decorator'
import { mapState, mapStores } from 'pinia'
import { useTableSlashPickerStore, type TableSlashPickerStore } from '../../stores/TableSlashPickerStore'

const ARROWS: Record<string, [number, number]> = {
  ArrowUp: [-1, 0],
  ArrowDown: [1, 0],
  ArrowLeft: [0, -1],
  ArrowRight: [0, 1]
}

@Component({
  computed: {
    ...mapStores(useTableSlashPickerStore),
    ...mapState(useTableSlashPickerStore, [
      'visible',
      'hoverRows',
      'hoverColumns',
      'resizing',
      'gridRows',
      'gridColumns',
      'displayRows',
      'displayColumns'
    ])
  }
})
export default class TableSlashSizePicker extends Vue {
  @Ref() readonly panel?: HTMLElement

  tableSlashPickerStore!: TableSlashPickerStore
  private visible!: boolean
  private hoverRows!: number
  private hoverColumns!: number
  private gridRows!: number
  private gridColumns!: number
  /** Where focus was when the picker opened; it goes back there when the picker is dismissed */
  private returnFocusTo: HTMLElement | null = null

  /** Built once per grid size (a computed), not per hover: the labels are not re-translated on every pointer move */
  get gridCells(): { key: string; row: number; col: number; label: string }[] {
    const cells: { key: string; row: number; col: number; label: string }[] = []
    for (let row = 1; row <= this.gridRows; row++) {
      for (let col = 1; col <= this.gridColumns; col++) {
        const label = this.$t('table.sizePickerCell', { rows: row, columns: col }) as string
        cells.push({ key: `${row}-${col}`, row, col, label })
      }
    }
    return cells
  }

  get gridStyle() {
    // Variables, not the template itself: without them the stylesheet still lays out the usual 10 × 20
    return { '--picker-columns': this.gridColumns, '--picker-rows': this.gridRows }
  }

  @Watch('visible') onVisibleChange(visible: boolean) {
    if (!visible) return
    this.returnFocusTo = document.activeElement instanceof HTMLElement ? document.activeElement : null
    this.$nextTick(() => this.focusActiveCell())
  }

  activeCell(): HTMLElement | null {
    return this.panel?.querySelector<HTMLElement>(`[data-cell="${this.hoverRows}-${this.hoverColumns}"]`) ?? null
  }

  focusActiveCell() {
    this.activeCell()?.focus()
  }

  onGridKeydown(event: KeyboardEvent) {
    const step = ARROWS[event.key]
    if (!step) return
    event.preventDefault()
    this.setHover(this.hoverRows + step[0], this.hoverColumns + step[1])
    this.$nextTick(() => this.focusActiveCell())
  }

  /** Tab cycles through the grid's current cell and the footer buttons; focus never leaves the dialog. */
  trapTab(event: KeyboardEvent) {
    const stops = [this.activeCell(), ...(this.panel?.querySelectorAll<HTMLElement>('.actions button') ?? [])].filter(
      (el): el is HTMLElement => !!el
    )
    if (!stops.length) return
    const index = stops.indexOf(document.activeElement as HTMLElement)
    const next = event.shiftKey ? (index <= 0 ? stops.length - 1 : index - 1) : (index + 1) % stops.length
    event.preventDefault()
    stops[next].focus()
  }

  setHover(row: number, col: number) {
    this.tableSlashPickerStore.setHover(row, col)
  }

  resetHover() {
    this.tableSlashPickerStore.resetHover()
  }

  confirmAt(row: number, col: number) {
    this.setHover(row, col)
    this.tableSlashPickerStore.confirm()
  }

  confirmDefault() {
    this.tableSlashPickerStore.confirmDefault()
  }

  close() {
    this.tableSlashPickerStore.close()
    const target = this.returnFocusTo
    this.returnFocusTo = null
    if (target?.isConnected) target.focus()
  }
}
</script>

<style lang="scss">
// The slash menu stays mounted until the table is inserted; keep it out of sight behind the picker
body:has(.table-slash-picker) [data-tippy-root] {
  visibility: hidden !important; // tippy sets visibility inline
}
</style>

<style scoped lang="scss">
@use '@/assets/styles' as *;

.table-slash-picker {
  position: fixed;
  inset: 0;
  // Above tippy's default 9999, or the slash menu it was opened from paints over the picker
  z-index: 10000;
  display: flex;
  align-items: center;
  justify-content: center;

  .backdrop {
    position: absolute;
    inset: 0;
    background: rgba(0, 0, 0, 0.2);
  }

  .panel {
    position: relative;
    background: $notch-color-neutral-grey-000;
    border-radius: $notch-radius-4x;
    padding: $notch-space-6x;
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
    max-width: 90vw;
  }

  .title {
    @include notch-text-label-strong-M;
    margin: 0 0 $notch-space-2x;
  }

  .size-label {
    @include notch-text-paragraph-regular-S;
    margin: 0 0 $notch-space-4x;
    color: $notch-color-neutral-sand-600;
  }

  .grid {
    display: grid;
    grid-template-columns: repeat(var(--picker-columns, 10), 16px);
    grid-template-rows: repeat(var(--picker-rows, 20), 16px);
    gap: 4px;
    // A large table being resized offers more rows than fit
    max-height: 60vh;
    overflow: auto;
    margin-bottom: $notch-space-4x;
  }

  .cell {
    width: 16px;
    height: 16px;
    border: 1px solid $notch-color-neutral-sand-200;
    border-radius: 2px;
    padding: 0;
    cursor: pointer;
    background: $notch-color-neutral-grey-000;

    &.active {
      background: $notch-color-semantic-accent-150;
      border-color: $notch-color-semantic-accent-500;
    }
  }

  .actions {
    display: flex;
    gap: $notch-space-3x;
    justify-content: flex-end;
  }
}
</style>
